/** Unified card surface. Use surface="glass" for translucent cards, surface="panel" for solid dark dashboard panels.
 *  Set bareChildren on glass cards that manage their own z-stacking (absolute-positioned overlays as direct siblings of content). */

import { ReactNode, CSSProperties, useState, useRef, MouseEvent as ReactMouseEvent } from 'react';

export type CardSurface = 'glass' | 'panel';

export type CardProps = {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  onClick?: () => void;
  /** 'glass' = translucent .glass-card. 'panel' = solid dark .glass-container. Default 'glass'. */
  surface?: CardSurface;
  /** Mouse-tracking radial glow on hover. Default false (enterprise UI rule). Glass surface only. */
  glow?: boolean;
  /** Lift + shadow on hover (glass surface). Default true for glass, false for panel. */
  hover?: boolean;
  /** Border radius. Default 'lg'. */
  rounded?: 'lg' | 'xl';
  /** Padding class. Default 'p-4' for glass, 'p-0' for panel. */
  padding?: string;
  /** Max-height class. Default 'max-h-[400px]' for glass, undefined for panel. */
  maxHeight?: string;
  /** Overflow class. Default 'overflow-auto' for glass, 'overflow-visible' for panel. */
  overflow?: string;
  /** When true, render children directly without the inner flex-col wrapper.
   *  Use for components that manage their own z-stacking (e.g., absolute-positioned
   *  hover overlays as siblings of content). Glass surface only; ignored when glow is true. */
  bareChildren?: boolean;
  /** KPI-card style: soft shadow + subtle gradient overlay that fades in on hover.
   *  Sets up an unnamed `group` so descendants can use `group-hover:*`. Glass surface only. */
  interactive?: boolean;
};

export const Card = ({
  children,
  className = '',
  style,
  onClick,
  surface = 'glass',
  glow = false,
  hover,
  rounded = 'lg',
  padding,
  maxHeight,
  overflow,
  bareChildren = false,
  interactive = false,
}: CardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePosition, setMousePosition] = useState({ x: 50, y: 50 });

  const isGlass = surface === 'glass';
  const hoverEnabled = hover ?? isGlass;
  const showGlow = glow && isGlass;

  const surfaceClass = isGlass ? 'glass-card' : 'glass-container';
  const roundedClass = rounded === 'xl' ? 'rounded-xl' : 'rounded-lg';
  const paddingClass = padding ?? (isGlass ? 'p-4' : 'p-0');
  const maxHeightClass = isGlass ? (maxHeight ?? 'max-h-[400px]') : (maxHeight ?? '');
  const overflowClass = overflow ?? (isGlass ? 'overflow-auto' : 'overflow-visible');
  const hoverModifier = isGlass && !hoverEnabled ? 'no-hover hover:!transform-none' : '';
  const cursorClass = onClick ? 'cursor-pointer' : '';
  const glowGroupClass = showGlow ? 'group/card relative' : '';
  const interactiveEnabled = interactive && isGlass;
  const interactiveClass = interactiveEnabled ? 'kpi-soft group relative' : '';
  const useBareChildren = bareChildren || interactiveEnabled;

  const finalClassName = [
    surfaceClass,
    roundedClass,
    paddingClass,
    maxHeightClass,
    overflowClass,
    hoverModifier,
    cursorClass,
    glowGroupClass,
    interactiveClass,
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

  const handleMouseMove = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!showGlow || !cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
  };

  if (!isGlass) {
    return (
      <div onClick={onClick} style={style} className={finalClassName}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={cardRef}
      onMouseMove={showGlow ? handleMouseMove : undefined}
      onClick={onClick}
      style={style}
      className={finalClassName}
    >
      {showGlow && (
        <>
          <span
            className="absolute inset-0 opacity-0 group-hover/card:opacity-100 transition-opacity duration-500 pointer-events-none rounded-lg"
            style={{
              background: `radial-gradient(circle 150px at ${mousePosition.x}% ${mousePosition.y}%, rgba(255, 255, 255, 0.05), transparent 80%)`,
            }}
          />
          <span
            className="absolute inset-0 opacity-0 group-hover/card:opacity-100 transition-opacity duration-700 pointer-events-none rounded-lg"
            style={{
              background: `linear-gradient(115deg, transparent ${mousePosition.x - 25}%, rgba(255, 255, 255, 0.04) ${mousePosition.x}%, transparent ${mousePosition.x + 25}%)`,
            }}
          />
        </>
      )}
      {interactiveEnabled && (
        <span
          aria-hidden
          className="absolute inset-0 z-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none rounded-[inherit]"
        />
      )}
      {useBareChildren && !showGlow ? (
        interactiveEnabled ? <div className="relative z-10">{children}</div> : children
      ) : (
        <div className="relative z-10 h-full flex flex-col">{children}</div>
      )}
    </div>
  );
};
